/**
 * Modal "Mi perfil": editar nombre + subir/quitar foto de perfil.
 *
 * Flujo del avatar:
 * 1. Usuario elige imagen.
 * 2. Pedimos presign → PUT directo a Garage → commit del attachment.
 * 3. PATCH /api/auth/me {avatar_attachment_id} → backend guarda referencia.
 * 4. Refrescamos /me y forzamos cache-bust de los <img> con timestamp.
 */
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Upload } from "lucide-react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Dialog } from "./ui/Dialog";
import { Avatar } from "./Avatar";
import { authApi } from "@/api/auth";
import { attachmentsApi, uploadFile } from "@/api/attachments";
import { useAuthStore } from "@/stores/auth";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ProfileDialog({ open, onClose }: Props) {
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const qc = useQueryClient();
  const [name, setName] = useState(me?.full_name ?? "");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [clearAvatar, setClearAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Reset SOLO cuando el modal abre. No resetear cuando `me` cambia (eso
  // borra lo que el usuario está escribiendo si /me se refresca en background).
  // biome-ignore lint/correctness/useExhaustiveDependencies: ver comentario arriba
  useEffect(() => {
    if (!open) return;
    setName(me?.full_name ?? "");
    setPreviewUrl(null);
    setPendingFile(null);
    setClearAvatar(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Solo se permiten imágenes.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("La imagen debe pesar menos de 5 MB.");
      return;
    }
    setError(null);
    setPendingFile(f);
    setClearAvatar(false);
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      let avatar_attachment_id: string | undefined;
      if (pendingFile) {
        const presign = await attachmentsApi.presign({
          mime: pendingFile.type,
          size_bytes: pendingFile.size,
          original_filename: pendingFile.name,
        });
        await uploadFile(pendingFile, presign.upload_url);
        await attachmentsApi.commit(presign.attachment_id);
        avatar_attachment_id = presign.attachment_id;
      }
      const payload: {
        full_name?: string;
        avatar_attachment_id?: string;
        clear_avatar?: boolean;
      } = {};
      if (name && name !== me?.full_name) payload.full_name = name;
      if (avatar_attachment_id) payload.avatar_attachment_id = avatar_attachment_id;
      if (clearAvatar) payload.clear_avatar = true;
      if (Object.keys(payload).length === 0) return me!;
      return await authApi.updateMe(payload);
    },
    onSuccess: (updated) => {
      setMe(updated);
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["room-members"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!me) return null;

  const currentAvatarUrl = clearAvatar
    ? null
    : previewUrl ?? (me.avatar_url ? `${me.avatar_url}&t=${Date.now()}` : null);

  return (
    <Dialog open={open} onClose={onClose} title="Mi perfil">
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <Avatar
            name={me.full_name}
            seed={me.id}
            size="xl"
            url={currentAvatarUrl ?? undefined}
          />
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="size-4" /> Cambiar foto
            </Button>
            {(me.avatar_url || previewUrl) && (
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600"
                onClick={() => {
                  setPendingFile(null);
                  setPreviewUrl(null);
                  setClearAvatar(true);
                }}
              >
                <Trash2 className="size-4" /> Quitar foto
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileChange}
            />
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Nombre completo</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </label>

        <div>
          <span className="mb-1 block text-sm font-medium text-slate-700">Email</span>
          <Input value={me.email} disabled />
          <p className="mt-1 text-xs text-slate-500">
            El email no se puede cambiar desde acá. Pedile al admin si necesitás otro.
          </p>
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium text-slate-700">Rol</span>
          <Input value={me.role} disabled />
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
